import { Outlet, useRoutes } from 'react-router-dom';

import { Landing, NotAuthorizedPage, NotFoundPage } from '@/features/misc';
import { useAuth } from '@/lib/auth';

import { protectedRoutes } from './protected';
import { AppContextProvider } from '@/context/AppContext';
import { Suspense } from 'react';
import { Spinner } from '@primer/react';
import { lazyImport } from '@/utils/lazyImport';
import { MainLayout } from '@/components/Layout';
import storage from '@/utils/storage';

const { PlaygroundWrapper } = lazyImport(
  () => import('@/features/playgorund/routes/PlaygroundWrapper'),
  'PlaygroundWrapper'
);
const { Waitlist } = lazyImport(() => import('@/features/misc'), 'Waitlist');
// const { Pricing } = lazyImport(() => import('@/features/pricing'), 'Pricing');
const { App } = lazyImport(() => import('@/features/app'), 'App');

const { AuthRoutes } = lazyImport(() => import('@/features/auth'), 'AuthRoutes');

export const AppRoutes = () => {
  const auth = useAuth();

  const PlaygroundPage = () => {
    return (
      <AppContextProvider>
        <PlaygroundWrapper activeRepoCluster={null} loadingRepoSSE={false}>
          <Suspense
            fallback={
              <div className="h-full w-full flex items-center justify-center">
                <Spinner size="large" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </PlaygroundWrapper>
      </AppContextProvider>
    );
  };

  const WaitlistPage = () => {
    return (
      <AppContextProvider>
        <Waitlist>
          <Suspense
            fallback={
              <div className="h-full w-full flex items-center justify-center">
                <Spinner size="large" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </Waitlist>
      </AppContextProvider>
    );
  };

  const NotFound = () => {
    return (
      <NotFoundPage>
        <Suspense
          fallback={
            <div className="h-full w-full flex items-center justify-center">
              <Spinner size="large" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </NotFoundPage>
    );
  };

  const AppPage = () => {
    return (
      <AppContextProvider>
        <MainLayout>
          <App>
            <Suspense
              fallback={
                <div className="h-full w-full flex items-center justify-center">
                  <Spinner size="large" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </App>
        </MainLayout>
      </AppContextProvider>
    );
  };

  const commonRoutes = [
    {
      path: '/',
      element: (
        <AppContextProvider>
          <Landing />
        </AppContextProvider>
      ),
    },
    {
      path: '/auth/*',
      element: <AuthRoutes />,
    },
    { path: 'playground', element: <PlaygroundPage /> },
    { path: 'waitlist', element: <WaitlistPage /> },
    // { path: 'pricing', element: <PricingPage /> },
    { path: 'app/projects/:projectId', element: <AppPage /> },
    { path: 'not-authorized', element: <NotAuthorizedPage /> },
    // { path: 'plan-ended', element: <PlanEndedPage /> },
    { path: 'not-found', element: <NotFound /> },
    { path: '*', element: <NotFound /> },
  ];

  const routes =
    (auth?.user || storage.getRefreshToken()) &&
    (storage.getOnboardingState() === 'incomplete' || !storage.getOnboardingState()) &&
    (storage.getMigrationStatus() !== 'migrate' || !storage.getMigrationStatus())
      ? protectedRoutes
      : commonRoutes;

  const element = useRoutes([...routes, ...commonRoutes]);

  return <>{element}</>;
};
